import { Helmet } from "react-helmet";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { omitBy, isNil, isEmpty, map } from "lodash";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useQuery } from "Hooks/index";
import useSession from "Hooks/session.hook";
import usePagination from "Hooks/pagination.hook";
import { encode, toaster, toNum } from "Helpers/index";
import { SortingMode } from "Types/common.types";
import {
  listIncidentReports,
  updateIncidentReportStatus,
  fetchReportStatisticsCount,
} from "Services/incident-report.services";
import "../incident-report.scss";
import { Row, Col } from "antd";
import { Tabs, SelectInput } from "@life-line-lab/components-react";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import StatisticBox from "Components/shared/statistic-box";
import IncidentReportList from "Components/incident-report/incident-report-table/incident-report-table";
import { fetchOrderSettingList } from "Services/order-setting.service";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import {
  fetchArchiveSettings,
  updateArchiveSettings,
} from "Services/order.service";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { OrderSettingsEnum } from "Data/settings";
import useCheckUserRole from "Hooks/check-user-role.hook";
import { UserRoleToAssign } from "Types/user.types";

const reportModel = (data) => {
  const { profileData, typeData, ...otherData } = data || {};

  return {
    "Report Code": otherData?.reportCode,
    Category: toCapitalisedSpaced(otherData?.category),
    Info: otherData?.info,
    Remarks: otherData?.remarks,
    "Issue Type": typeData[0]?.name,
    "Created Date": otherData?.createdAt
      ? moment(otherData?.createdAt).format("DD-MM-YYYY hh:mm")
      : null,
    "Created By": profileData?.fullName,
    "Phone Number": profileData?.primaryPhone?.fullNumber,
    "Updated Date": otherData?.updatedAt
      ? moment(otherData?.updatedAt).format("DD-MM-YYYY hh:mm")
      : null,
    "Updated By": profileData?.fullName,
    Status: toCapitalisedSpaced(otherData?.status),
  };
};

// ======================================
// Logic
// ======================================

const useFetchStatistics = (statisticsBoxes) => {
  const [parcelStatistics, setParcelStatistics] =
    useState<any>(statisticsBoxes);
  const { org } = useSession();

  useEffect(() => {
    getStatCount();
  }, []);

  const getStatCount = async () => {
    const response = await fetchReportStatisticsCount(org._id);
    if (response.incidentReportStatistics) {
      let parcelStat = map(parcelStatistics, (statObj) => {
        statObj.count = response.incidentReportStatistics[statObj.key];
        return statObj;
      });
      setParcelStatistics(parcelStat);
    }
  };

  return {
    parcelStatistics,
  };
};

const useArchiveSettings = () => {
  const [archiveState, setArchiveState] = useState(false);
  const [archiveSettings, setArchiveSettings] = useState<any>([]);

  const { org } = useSession();

  //  Get archived settings
  // =============================================
  const handleFetchArchiveSettings = async (orgID) => {
    const responseData: any = await fetchArchiveSettings(
      orgID,
      "INCIDENT_REPORT",
    );

    if (!isNil(responseData.data) && !responseData.error) {
      setArchiveSettings &&
        setArchiveSettings(responseData.data?.archiveSetting[0]);
    } else {
      toaster("error", "Failed to fetch archive settings.");
    }
  };

  //  Update archive settings
  // =============================================
  const handleUpdateArchiveSettings = async (archiveSettingsData) => {
    const responseData: any = await updateArchiveSettings(archiveSettingsData);

    if (!isNil(responseData.data) && !responseData.error) {
      toaster("success", "Archive updated successfully");
      handleFetchArchiveSettings(org._id);
    } else {
      toaster("error", responseData.error.message.message);
    }
  };

  return {
    allArchiveObjects: {
      archiveSettingsObject: { archiveSettings, setArchiveSettings },
      archiveStateObject: { archiveState, setArchiveState },
      handleFetchArchiveSettings,
      modifySettingsFunctions: { handleUpdateArchiveSettings },
    },
  };
};

const useIncidentReport = (filterObj) => {
  const history = useHistory();
  const { org } = useSession();
  const { t } = useTranslation();

  const [typeOptions, setTypeOptions] = useState<any>([]);
  const [allReportList, setAllReportList] = useState<any>([]);
  const [archivedReportList, setArchivedReportList] = useState<any>([]);
  const [currentListCount, setCurrentListCount] = useState(0);

  const [isExcelLoading, setIsExcelLoading] = useState<boolean>(false);

  const { paginationComponent, itemPerPage, queryParams, setQueryParams } =
    usePagination({
      currentListCount,
      resetPageDeps: filterObj
    });

  // get all types for filter
  const typeFilters = {
    orgID: org._id,
    type: OrderSettingsEnum.ISSUE_TYPE,
    pagination: {
      offset: queryParams.offset,
      limit: itemPerPage,
    },
  };

  const [filters, setFilters] = useState<any>(typeFilters);

  useEffect(() => {
    handleFetchOrderSettingsList();
  }, []);

  const handleDownloadExcelFile = async (
    tableData: any[],
    fileName: string,
  ) => {
    try {
      const allData = await handleListIncidentReports(false, false);

      const mappedSheet = allData?.map((data) => reportModel(data));

      const worksheet = XLSX.utils.json_to_sheet(mappedSheet);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.ms-excel",
      });
      saveAs(fileData, `${fileName}.xlsx`);
    } catch (e) {
      console.log("Error generationg report.", e);
      toaster("error", t("error.failedGenerateReport"));
    }
  };

  const handleListIncidentReports = async (
    enforceLimit = true,
    isWeb = true,
  ) => {
    !isWeb && setIsExcelLoading && setIsExcelLoading(true);
    let filters: any = omitBy({ ...filterObj }, isNil);

    const responseData = await listIncidentReports(
      filters,
      enforceLimit
        ? {
            offset: queryParams.offset,
            limit: toNum(itemPerPage),
          }
        : null,
      { sortBy: "reportCode", mode: SortingMode.ASC },
    );
    if (!isNil(responseData) && !responseData.error) {
      if (isWeb) {
        setAllReportList && setAllReportList(responseData);
        setCurrentListCount(responseData.length);
      } else {
        setIsExcelLoading && setIsExcelLoading(false);
        return responseData;
      }
    } else {
      toaster("error", "Cannot fetch incident Report");
    }

    setIsExcelLoading && setIsExcelLoading(false);
  };

  const handleOpenIncidentReport = (reportID) => {
    let encodedId = encode(reportID);
    history.push(`/incident-reports/details/${encodedId}/view`);
  };

  const handleStatusUpdate = async (reportID, newStatus) => {
    const responseData = await updateIncidentReportStatus(reportID, newStatus);
    if (!responseData.error && responseData) {
      toaster("success", "Status updated successfully");
      handleListIncidentReports();
    } else {
      toaster("error", responseData.error.message);
    }
  };

  // needed to fetch every type of goods
  const handleFetchOrderSettingsList = async () => {
    const responseData = await fetchOrderSettingList(
      omitBy(typeFilters, isNil),
    );
    // partial type error comes from the order-setting.service
    if (responseData && !responseData.error) {
      setTypeOptions && setTypeOptions(responseData);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  return {
    isExcelLoading,
    filters,
    itemPerPage,
    reportLists: { allReportList, archivedReportList },
    typeOptions,
    paginationComponent,
    queryObjects: { queryParams, setQueryParams },
    handleDownloadExcelFile,
    handleListIncidentReports,
    handleOpenIncidentReport,
    handleStatusUpdate,
  };
};

const useFilters = (initialFilters) => {
  const [filterObj, setFilterObj] = useState<any>(initialFilters);

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      setFilterObj(initialFilters);
    } else {
      const filterValue = {
        ...filterObj,
        type: filterInput.type || null,
        category: filterInput.category || null,
        orFields: filterInput.orFields || null,
      };
      setFilterObj(filterValue);
    }
  };

  return {
    filterObj,
    setFilterObj,
    onFilter,
  };
};

const useSwitchTabs = (filterObjects, tabKey, page, queryObjects) => {
  const history = useHistory();

  const [tabActiveKey, setTabActiveKey] = useState(tabKey);
  const [isParamsCleared, setIsParamsCleared] = useState<boolean>(true); // prevent from calling API upon clearing queryParams

  const { initialFilters, setFilterObj } = filterObjects;
  const { queryParams, setQueryParams } = queryObjects;

  useEffect(() => {
    setFilterObj && setFilterObj(initialFilters);
    handleTabClick && handleTabClick(tabActiveKey);
    setIsParamsCleared(true);
  }, [tabActiveKey]);

  const handleTabClick = (key) => {
    setIsParamsCleared(false);

    let path = "";
    if (key === "0") {
      path = page
        ? `/incident-reports/report?${key}`
        : `/incident-reports/report`;
    } else {
      path = page
        ? `/incident-reports/report?tabKey=${key}&page=${page}`
        : `/incident-reports/report?tabKey=${key}`;
    }

    setTabActiveKey(key);
    history.push(path);
    setQueryParams && setQueryParams({ ...queryParams, offset: 0, page: 1 });
  };

  const handleStatClick = (key: string) => {
    handleTabClick(key);
  };

  return {
    tabActiveKey,
    isParamsCleared,
    handleTabClick,
    handleStatClick,
  };
};

// ======================================
// Sub Component
// ======================================

const StatisticsBoxes = (props: any) => {
  const { parcelStatistics, handleStatClick } = props || {};

  return (
    <>
      {parcelStatistics.map((parcelStat, index) => {
        return (
          <StatisticBox
            key={index}
            statObj={parcelStat}
            count={parcelStat.count}
            context={""}
            onClick={handleStatClick}
            icon={parcelStat.icon}
            iconSrc={parcelStat.iconSrc}
          />
        );
      })}
    </>
  );
};

// ======================================
// Main Component
// ======================================

/*
  Small easter egg for developers:
  Former vice main FE for logistics( 1/3 of the dynamic trio), and main BE  (1/3 of the dynamic trio)
  Jazz and Adam's favorite module was incident report 😀
*/

const IncidentReportPage = () => {
  const query = useQuery();
  const { t } = useTranslation();
  const tabKey = query.get("tabKey") || "0";
  const page: any = query.get("page") || 1;

  const statisticsBoxes = [
    {
      key: "openIncidentReports",
      name: "openIncidentReports",
      tab: "0",
      count: 0,
      iconSrc: "/assets/icons/openIncident.svg",
    },
    {
      key: "pendingIncidentReports",
      name: "pendingIncidentReports",
      tab: "1",
      count: 0,
      iconSrc: "/assets/icons/pendingIncident.svg",
    },
    {
      key: "closedIncidentReports",
      name: "closedIncidentReports",
      tab: "2",
      count: 0,
      iconSrc: "/assets/icons/closedIncident.svg",
    },
  ];

  const tabStatus = {
    "0": "OPEN",
    "1": "PENDING",
    "2": "CLOSED",
  };

  const initialFilters = {
    status: tabStatus[tabKey],
  };

  const { parcelStatistics } = useFetchStatistics(statisticsBoxes);
  const { filterObj, setFilterObj, onFilter } = useFilters(initialFilters);
  const filterObjects = { initialFilters, filterObj, setFilterObj };
  const { allArchiveObjects } = useArchiveSettings();
  const { isRole } = useCheckUserRole();
  const {
    isExcelLoading,
    itemPerPage,
    reportLists,
    typeOptions,
    paginationComponent,
    queryObjects,
    handleDownloadExcelFile,
    handleListIncidentReports,
    handleOpenIncidentReport,
    handleStatusUpdate,
  } = useIncidentReport(filterObj);
  const { allReportList, archivedReportList } = reportLists || {};
  const { queryParams } = queryObjects || {};
  const { tabActiveKey, isParamsCleared, handleTabClick, handleStatClick } =
    useSwitchTabs(filterObjects, tabKey, page, queryObjects);

  let tableColumns = [
    {
      name: "reportCode",
      dataIndex: "reportCode",
      title: t("label.reportCode").toUpperCase(),
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.reportCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenIncidentReport(record._id);
          },
        };
      },
    },
    {
      name: "category",
      dataIndex: "category",
      title: t("label.category").toUpperCase(),
    },
    {
      name: "info",
      dataIndex: "info",
      title: t("label.info").toUpperCase(),
    },
    {
      name: "type",
      dataIndex: "type",
      title: t("label.type").toUpperCase(),
      render: (_: any, record: any) => {
        if (record?.typeData == null) {
          return <>{record?.type}</>;
        } else {
          return record?.typeData.map((types, index) => {
            return (
              <Row key={index}>
                <Col>{types?.name}</Col>
              </Row>
            );
          });
        }
      },
    },
    {
      name: "remarks",
      dataIndex: "remarks",
      title: t("label.remarks").toUpperCase(),
    },
    {
      name: "raisedBy",
      dataIndex: "createdBy",
      title: t("label.raisedBy").toUpperCase(),
      render: (_: any, record: any) => {
        return (
          <div>
            <span>{record.profileData?.fullName}</span>
          </div>
        );
      },
    },
    {
      name: "status",
      dataIndex: "status",
      title: t("label.status").toUpperCase(),
      render: (_: any, record: any) => {
        return (
          <div className="select-status" style={{ overflow: "auto" }}>
            <SelectInput
              className={`${record.status}`}
              value={record.status.replaceAll("_", " ")}
              options={[
                {
                  label: t("label.open"),
                  value: "OPEN",
                },
                {
                  label: t("label.pending"),
                  value: "PENDING",
                },
                {
                  label: t("label.closed"),
                  value: "CLOSED",
                },
              ]}
              onChange={(value) => {
                handleStatusUpdate(record._id, value);
              }}
            />
          </div>
        );
      },
    },
  ];

  const listProps = {
    columns: tableColumns,
    allReportList: allReportList,
    onDownloadExcelFile: handleDownloadExcelFile,
    onFilter: onFilter,
    typeOptions: typeOptions,
    isExcelLoading: isExcelLoading,
  };

  const allowArchive = () => {
    let isAllowed = true;

    if (isRole([UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER]))
      isAllowed = false;

    return isAllowed;
  };

  let tabs = [
    {
      tabKey: "0",
      label: "New",
      component: <IncidentReportList tabLabel="New" {...listProps} />,
    },
    {
      tabKey: "1",
      label: "Pending",
      component: <IncidentReportList tabLabel="Pending" {...listProps} />,
    },
    {
      tabKey: "2",
      label: "Closed",
      component: (
        <IncidentReportList
          tabLabel="Closed"
          allArchivedReportList={archivedReportList}
          hasArchive={allowArchive()}
          allArchiveObjects={allArchiveObjects}
          {...listProps}
        />
      ),
    },
  ];

  useEffect(() => {
    isParamsCleared === true && handleListIncidentReports();
  }, [filterObj, queryParams, itemPerPage]);

  return (
    <div className="incident-report">
      <Helmet>
        <title>{t("nav.incidentReports")}</title>
      </Helmet>
      <ModuleTitleBar titleText={t("title.incidentReports")} />
      <Row className="statistics-container">
        <Col span={24}>
          <div className="tracking-list-statistics-group">
            <StatisticsBoxes
              parcelStatistics={parcelStatistics}
              handleStatClick={handleStatClick}
            />
          </div>
        </Col>
      </Row>
      <ErrorBoundary>
        <Tabs
          destroyInactiveTabPane={true}
          onTabClick={handleTabClick}
          defaultActiveKey="0"
          activeKey={tabActiveKey}
          tabBarGutter={25}
          tabs={tabs}
        />
        <div className="my-30">{paginationComponent}</div>
      </ErrorBoundary>
    </div>
  );
};

export default IncidentReportPage;
